import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { CRAComplianceData, CRAState } from "types/craCompliance";

const CRA_STATUS_DEFAULTS: Record<string, string> = {
  exploitStatus: "Unknown",
  notificationStatus: "Not Required",
  userDisclosureStatus: "Not Required",
};

export function getCRADefaultStatus(key: string): string {
  return CRA_STATUS_DEFAULTS[key] ?? "";
}

export const EXPLOIT_STATUS_OPTIONS = [
  { label: "Unknown", value: "Unknown" },
  { label: "No Exploit", value: "No Exploit" },
  { label: "Proof-of-concept", value: "Proof-of-concept" },
  { label: "Exploit Available", value: "Exploit Available" },
  { label: "Known Exploited", value: "Known Exploited" },
  { label: "Exploitable", value: "Exploitable" },
  { label: "Actively Exploited", value: "Actively Exploited" },
];

export const NOTIFICATION_STATUS_OPTIONS = [
  { label: "Not Required", value: "Not Required" },
  { label: "Pending Review", value: "Pending Review" },
  { label: "Pending Notification", value: "Pending Notification" },
  { label: "Early Warning Released", value: "Early Warning Released" },
];

export const USER_DISCLOSURE_STATUS_OPTIONS = [
  { label: "Not Required", value: "Not Required" },
  { label: "Pending", value: "Pending" },
  { label: "Delayed", value: "Delayed" },
  { label: "Notified", value: "Notified" },
];

export const CRA_FIELD_LABELS = {
  exploitStatus: "Exploit Status",
  earlyWarningDue: "Early Warning Due",
  notificationDue: "Notification Due",
  finalReportDue: "Final Report Due",
  notificationStatus: "Notification Status",
  userDisclosureStatus: "User Disclosure Status",
  userDisclosureDue: "User Disclosure Due",
  remediationEvidence: "Remediation Evidence",
};

export const CRA_DATE_FIELDS = new Set([
  "earlyWarningDue",
  "notificationDue",
  "finalReportDue",
  "userDisclosureDue",
]);

export const DEFAULT_CRA_STATE: CRAState = {
  exploitStatus: null,
  notificationStatus: null,
  userDisclosureStatus: null,
  earlyWarningDue: null,
  notificationDue: null,
  finalReportDue: null,
  userDisclosureDue: null,
  remediationEvidence: "",
};

export function craDataToState(craData: CRAComplianceData): CRAState {
  return {
    exploitStatus: craData.exploitStatus,
    notificationStatus: craData.notificationStatus,
    userDisclosureStatus: craData.userDisclosureStatus,
    earlyWarningDue: epochToDayjs(craData.earlyWarningDue),
    notificationDue: epochToDayjs(craData.notificationDue),
    finalReportDue: epochToDayjs(craData.finalReportDue),
    userDisclosureDue: epochToDayjs(craData.userDisclosureDue),
    remediationEvidence: craData.remediationEvidence ?? "",
  };
}

export function craStateToApiData(state: CRAState): CRAComplianceData {
  return {
    exploitStatus: state.exploitStatus,
    notificationStatus: state.notificationStatus,
    userDisclosureStatus: state.userDisclosureStatus,
    earlyWarningDue: state.earlyWarningDue?.valueOf() ?? null,
    notificationDue: state.notificationDue?.valueOf() ?? null,
    finalReportDue: state.finalReportDue?.valueOf() ?? null,
    userDisclosureDue: state.userDisclosureDue?.valueOf() ?? null,
    remediationEvidence: state.remediationEvidence || null,
  };
}

export function epochToDayjs(epoch: number | null | undefined): Dayjs | null {
  if (!epoch) return null;
  return dayjs(epoch);
}

export const FIELD_TOOLTIPS: Record<string, React.ReactElement | string> = {
  exploitStatus: (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <b>Unknown:</b> The default status; the exploit status has not yet been
        determined.
      </div>
      <div>
        <b>No exploit:</b> The vulnerability exists, but no known code to
        exploit it is available.
      </div>
      <div>
        <b>Proof-of-concept:</b> Publicly available Proof-of-Concept (PoC)
        exploit code exists, but no active attacks have been observed.
      </div>
      <div>
        <b>Available:</b> High-quality exploit code (e.g., in Metasploit, Cobalt
        Strike) is available, facilitating easy use by attackers, even if mass
        active attacks are not yet confirmed.
      </div>
      <div>
        <b>Known Exploited:</b> Documented evidence exists that the
        vulnerability has been exploited in real-world attacks (e.g., CISA KEV
        listing, threat intelligence reports), though it may not be currently
        active or confirmed within your specific environment.
      </div>
      <div>
        <b>Exploitable:</b> Technical analysis confirms the vulnerability is
        exploitable within your specific environment based on asset
        configuration, accessibility, and attack surface (e.g., internet-facing,
        authentication not required, vulnerable component is actively used).
        (NOTE: Requires disclosure but not 24-hour notification)
      </div>
      <div>
        <b>Actively Exploited:</b> Reliable evidence confirms that the
        vulnerability is currently being exploited maliciously and affecting
        your product (NOTE: only this triggers 24-hour ENISA reporting
        requirements)
      </div>
    </div>
  ),
  notificationStatus: (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <b>Not Required:</b> This finding does not comes under CRA. Default
        Status.
      </div>
      <div>
        <b>Pending Review:</b> The vulnerability is awaiting review by the
        security analyst.
      </div>
      <div>
        <b>Pending Notification:</b> The analyst has confirmed that notification
        is required and is currently pending.
      </div>
      <div>
        <b>Early Warning Released:</b> The early warning has been sent typically
        within 24 hours of the vulnerability becoming known and exploited.
      </div>
    </div>
  ),
  earlyWarningDue:
    "The due date for issuing an early warning notification to the relevant authority. This deadline is set at 24 hours from the time the CRA Notification Status transitions to Pending Notification.",
  notificationDue:
    "Used to track the due date for releasing vulnerability notification to the respective authority. This will be tracked as 72 hours from the time when the CRA Notification Status changes to Pending Notification.",
  finalReportDue:
    "The due date for releasing the final report to the relevant authority. This date is set at 14 days after the corrective measure or remediation for the vulnerability becomes available.",
  userDisclosureStatus:
    "The CRA mandates that organizations disclose vulnerabilities to their users, primarily when a corrective measure is available, enabling users to implement those measures. Use this field to track the user disclosure workflow, with possible values being: Pending, Delayed, or Notified.",
  userDisclosureDue:
    "The due date / planned date for releasing the disclosure to the users.",
  remediationEvidence:
    "This field is for capturing evidence or mitigation measures taken to remediate the finding. This information will be included in the Vulnerability Notification and Final Report that the organization provides to the authorities.",
};

export const TOOLTIP_OVERLAY_STYLE = {
  maxWidth: 600,
  maxHeight: 300,
  overflowY: "auto" as const,
};
